import { defineStore } from 'pinia';

import { readCustomerName } from '@/shared/composables/bingo/customer-name';
import {
  refreshBingoToken,
  tryDecodeTokenClaims
} from '@/shared/composables/bingo/token';

type BingoSessionCustomer = {
  id: number;
  name: string;
  lastname: string;
};

type BingoSessionState = {
  session_id: number | null;
  token: string | null;
  customer: BingoSessionCustomer | null;
  identityRequest: number;
  lang: string | null;
  name: string | null;
};

// Machinery, not state: a dead pass 401s every call that fires after it expires, and a
// card fetch and a chat poll can land within milliseconds of each other. Without this
// each one mints its own pass and they race to setToken.
let inFlightRefresh: Promise<string | null> | null = null;

export const useBingoSessionStore = defineStore('bingoSession', {
  state: (): BingoSessionState => ({
    session_id: null,
    token: null,
    customer: null,
    identityRequest: 0,
    lang: null,
    name: null
  }),
  getters: {
    sessionId: (state) => state.session_id,
    uuid: (state) =>
      state.token ? (tryDecodeTokenClaims(state.token)?.uuid ?? null) : null
  },
  actions: {
    setSessionId(sessionId: number) {
      this.session_id = sessionId;
    },
    setToken(token: string) {
      this.token = token;
    },
    requestIdentityRefresh() {
      this.identityRequest += 1;
    },
    setSessionMeta(meta: { lang: string; name: string }) {
      this.lang = meta.lang;
      this.name = meta.name;
    },
    setCustomerData(customer: BingoSessionCustomer) {
      // The bingo API can report an empty name for a customer Shopify has a
      // name for, so fall back to the value the section rendered server-side.
      // Applied here rather than at each call site so the title, the lobby
      // greeting and the chat author label all see the same resolved name.
      const fromLiquid = readCustomerName();

      this.customer = {
        id: customer.id,
        name: customer.name?.trim() || fromLiquid.first || '',
        lastname: customer.lastname?.trim() || fromLiquid.last || ''
      };
    },
    async refresh() {
      if (inFlightRefresh) {
        return inFlightRefresh;
      }

      inFlightRefresh = (async () => {
        const token = await refreshBingoToken();
        if (!token) {
          this.logError('Failed to refresh the bingo token');
          return null;
        }
        this.setToken(token);
        return token;
      })();

      try {
        return await inFlightRefresh;
      } finally {
        inFlightRefresh = null;
      }
    },
    logInfo(message: unknown) {
      console.log(`${new Date()} - UUID: ${this.uuid}:`, message);
    },
    logError(message: unknown) {
      console.error(`${new Date()} - UUID: ${this.uuid}:`, message);
    },
    customerExists(reason: string) {
      const exists = !!this.customer;
      if (!exists) {
        this.logError(
          `Customer is ${this.customer}! - Attempt to use it for ${reason}`
        );
      }
      return exists;
    }
  }
});
